/**
 * usePageviewTracker — anonymous, cookieless, GDPR-friendly tracker.
 *
 * Mounts once at the App root. On every route change it sends a pageview
 * to POST /api/analytics/pageview. It maintains a sessionStorage-backed
 * session_id that rolls over after 30 minutes of inactivity. A heartbeat
 * fires every 30s while the tab is visible to keep last_seen_at fresh,
 * and a beforeunload beacon ensures the final last_seen is captured.
 *
 * Admin routes are never tracked.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${BACKEND}/api/analytics/pageview`;
const HEARTBEAT_ENDPOINT = `${BACKEND}/api/analytics/pageview/heartbeat`;
const SS_SID = 'wa_sid';
const SS_LAST_TS = 'wa_last_ts';
const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 min

const isAdminPath = (path) => path === '/admin' || path.startsWith('/admin/');

const newSessionId = () => {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return 'wa_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
};

const getOrCreateSessionId = () => {
  try {
    const stored = sessionStorage.getItem(SS_SID);
    const lastTsRaw = sessionStorage.getItem(SS_LAST_TS);
    const lastTs = lastTsRaw ? parseInt(lastTsRaw, 10) : 0;
    const now = Date.now();
    if (stored && lastTs && now - lastTs < IDLE_TIMEOUT_MS) {
      sessionStorage.setItem(SS_LAST_TS, String(now));
      return stored;
    }
    const fresh = newSessionId();
    sessionStorage.setItem(SS_SID, fresh);
    sessionStorage.setItem(SS_LAST_TS, String(now));
    return fresh;
  } catch (_e) {
    // sessionStorage disabled → use ephemeral in-memory id
    return newSessionId();
  }
};

const touchSession = () => {
  try {
    sessionStorage.setItem(SS_LAST_TS, String(Date.now()));
  } catch (_e) {
    /* noop */
  }
};

export const usePageviewTracker = () => {
  const location = useLocation();
  const lastPathRef = useRef(null);

  // 1. Send pageview on every route change (admin routes skipped)
  useEffect(() => {
    if (!BACKEND) return;
    const path = location.pathname;
    if (isAdminPath(path)) return;
    if (lastPathRef.current === path) return; // avoid duplicate triggers
    lastPathRef.current = path;

    const sid = getOrCreateSessionId();
    const body = {
      session_id: sid,
      path,
      referrer: document.referrer || null,
      screen_width: window.innerWidth || null,
      language: (navigator.language || '').slice(0, 16) || null,
    };
    // Fire-and-forget; never block the UI on analytics failures.
    axios.post(ENDPOINT, body, { timeout: 4000 }).catch(() => {});
  }, [location.pathname]);

  // 2. Heartbeat every 30s while document is visible
  useEffect(() => {
    if (!BACKEND) return;
    if (isAdminPath(location.pathname)) return;
    let timer = null;
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const sid = sessionStorage.getItem(SS_SID);
        if (!sid) return;
        touchSession();
        axios.post(HEARTBEAT_ENDPOINT, { session_id: sid, path: window.location.pathname }, { timeout: 4000 }).catch(() => {});
      } catch (_e) {
        /* noop */
      }
    };
    timer = setInterval(tick, 30_000);
    return () => clearInterval(timer);
  }, [location.pathname]);

  // 3. Final beacon on unload to capture exit duration
  useEffect(() => {
    if (!BACKEND) return;
    const flush = () => {
      try {
        const sid = sessionStorage.getItem(SS_SID);
        if (!sid) return;
        const payload = JSON.stringify({ session_id: sid, path: window.location.pathname });
        const blob = new Blob([payload], { type: 'application/json' });
        if (navigator.sendBeacon) {
          navigator.sendBeacon(HEARTBEAT_ENDPOINT, blob);
        }
      } catch (_e) {
        /* noop */
      }
    };
    window.addEventListener('beforeunload', flush);
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('beforeunload', flush);
      window.removeEventListener('pagehide', flush);
    };
  }, []);
};

export default usePageviewTracker;
